<template>
  <div class="tarja-comments-wrapper">
    <!-- SVG Background -->
    <div class="tarja-bg">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient
            id="comment-gradient"
            x1="175"
            y1="651.53"
            x2="762.8"
            y2="651.53"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stop-color="#520c8d" />
            <stop offset=".49" stop-color="#84276f" />
            <stop offset="1" stop-color="#bf133f" />
          </linearGradient>
        </defs>
        <!-- Transparent background (no black rectangle) -->
        <polygon
          fill="url(#comment-gradient)"
          points="160 902.94 695.5 902.94 742.8 978.60 695.59 1060 155 1059.93 179.85 987.46 160 902.94"
        />
      </svg>
    </div>

    <!-- Content Overlay -->
    <div class="content-overlay">
      <div class="comment-card" :style="cardPositionStyle">
        <!-- Header (Username) -->
        <div class="comment-header">
          <span class="comment-username" :style="usernameStyle">
            {{ displayName }}
          </span>
        </div>

        <!-- Content Section -->
        <div class="comment-content">
          <!-- Text Content -->
          <div v-if="isTextContent" class="comment-text" :style="textContentStyle">
            {{ item.dsc_texto_colaboracao }}
          </div>

          <!-- Audio Content -->
          <div v-else-if="isAudioContent" class="audio-wrapper">
            <div class="custom-audio-player">
              <audio
                ref="audioElement"
                :src="item.url_midia_colaboracao"
                @loadedmetadata="onAudioLoaded"
                @timeupdate="onTimeUpdate"
                @ended="onAudioEnded"
                @canplay="onCanPlay"
                autoplay
                preload="auto"
                style="display: none"
              />

              <div class="audio-controls">
                <button class="play-pause-btn" @click="togglePlayPause">
                  <span class="play-icon">{{ isPlaying ? "❚❚" : "▶" }}</span>
                </button>

                <div class="wave-timeline-container" @click="seekAudio">
                  <div class="wave-timeline">
                    <div
                      v-for="i in 40"
                      :key="i"
                      class="wave-bar"
                      :style="getWaveBarStyle(i)"
                    ></div>
                  </div>
                </div>

                <button class="speed-btn" @click="togglePlaybackSpeed">
                  <span class="speed-text">{{ playbackSpeed }}x</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TarjaTVZComments",

  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({
        id: 1,
        dsc_nome_colaborador: "@username",
        dsc_texto_colaboracao: "",
        dsc_tipo_midia_colaboracao: null,
        url_midia_colaboracao: null,
      }),
    },
    isFullScreen: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackSpeed: 1,
      availableSpeeds: [1, 1.5, 2],
      autoplayAttempted: false,
    };
  },

  computed: {
    displayName() {
      const name = this.item.dsc_nome_colaborador || "@username";
      // Ensure @ prefix
      return name.startsWith("@") ? name : `@${name}`;
    },

    isTextContent() {
      return (
        !this.item.dsc_tipo_midia_colaboracao ||
        this.item.dsc_tipo_midia_colaboracao === "text"
      );
    },

    isAudioContent() {
      return this.item.dsc_tipo_midia_colaboracao === "audio";
    },

    cardPositionStyle() {
      // Polygon bounds: x: 175-762.8, y: 573-730
      // Content positioned with proper padding from edges
      return {
        position: "absolute",
        left: "200px", // Padding from left edge (past the notch at 199.85)
        bottom: "40px", // 1080 - 730 + ~15px padding from bottom
        width: "490px", // Width to fit within polygon before arrow narrows
      };
    },

    usernameStyle() {
      return {
        fontFamily: "'Gobold Regular', 'Arial Black', sans-serif",
        fontSize: "22px",
        fontWeight: "700",
        color: "#ffffff",
        textTransform: "none",
        letterSpacing: "0.02em",
      };
    },

    textContentStyle() {
      const numChars = (this.item.dsc_texto_colaboracao || "").trim().length;
      let fontSize = "18px";
      let lineHeight = "1.35";

      if (numChars <= 100) {
        fontSize = "20px";
        lineHeight = "1.4";
      } else if (numChars <= 180) {
        fontSize = "18px";
        lineHeight = "1.35";
      } else if (numChars <= 240) {
        fontSize = "16px";
      } else {
        fontSize = "14px";
        lineHeight = "1.25";
      }

      return {
        fontFamily: "'Gobold Thin', 'Arial Narrow', sans-serif",
        fontSize,
        lineHeight,
        fontWeight: "600",
        color: "#ffffff",
        maxWidth: "480px",
        wordWrap: "break-word",
        overflowWrap: "break-word",
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.isAudioContent && this.$refs.audioElement) {
        this.attemptAutoplay();
      }
    });
  },

  methods: {
    async attemptAutoplay() {
      if (this.autoplayAttempted || !this.$refs.audioElement) return;

      this.autoplayAttempted = true;
      const audio = this.$refs.audioElement;

      try {
        await audio.play();
        this.isPlaying = true;
      } catch (error) {
        console.error("Autoplay prevented by browser:", error);
        this.isPlaying = false;
      }
    },

    togglePlayPause() {
      const audio = this.$refs.audioElement;
      if (this.isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      this.isPlaying = !this.isPlaying;
    },

    togglePlaybackSpeed() {
      const currentIndex = this.availableSpeeds.indexOf(this.playbackSpeed);
      const nextIndex = (currentIndex + 1) % this.availableSpeeds.length;
      this.playbackSpeed = this.availableSpeeds[nextIndex];

      if (this.$refs.audioElement) {
        this.$refs.audioElement.playbackRate = this.playbackSpeed;
      }
    },

    onAudioLoaded() {
      this.duration = this.$refs.audioElement.duration;
      this.$refs.audioElement.playbackRate = this.playbackSpeed;

      if (!this.autoplayAttempted) {
        this.attemptAutoplay();
      }
    },

    onCanPlay() {
      if (!this.autoplayAttempted) {
        this.attemptAutoplay();
      }
    },

    onTimeUpdate() {
      this.currentTime = this.$refs.audioElement.currentTime;
    },

    onAudioEnded() {
      this.isPlaying = false;
      this.currentTime = 0;
    },

    seekAudio(event) {
      const timeline = event.currentTarget;
      const rect = timeline.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      const seekTime = percent * this.duration;

      this.$refs.audioElement.currentTime = seekTime;
      this.currentTime = seekTime;
    },

    getWaveBarStyle(index) {
      const heights = [
        8,
        15,
        20,
        12,
        25,
        18,
        10,
        22,
        16,
        14,
        28,
        11,
        19,
        24,
        13,
        17,
        21,
        9,
        26,
        15,
      ];
      const height = heights[index % heights.length];

      const progressPercent =
        this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
      const barProgress = (index / 40) * 100;
      const isActive = barProgress <= progressPercent;

      return {
        width: "3px",
        height: `${height}px`,
        backgroundColor: isActive ? "#ffffff" : "rgba(255, 255, 255, 0.5)",
        borderRadius: "1px",
        transition: "background-color 0.1s ease",
      };
    },
  },

  watch: {
    item: {
      handler() {
        this.autoplayAttempted = false;
        this.$nextTick(() => {
          if (this.isAudioContent && this.$refs.audioElement) {
            this.attemptAutoplay();
          }
        });
      },
      deep: true,
    },

    isAudioContent(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          if (this.$refs.audioElement) {
            this.attemptAutoplay();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Gobold Regular";
  src: url("@/assets/Gobold_Regular.otf") format("opentype");
}

@font-face {
  font-family: "Gobold Thin";
  src: url("@/assets/Gobold_Thin.otf") format("opentype");
}

.tarja-comments-wrapper {
  position: relative;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
  overflow: hidden;
  pointer-events: none;
}

.tarja-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.content-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.comment-card {
  display: flex;
  flex-direction: column;
  width: 490px;
  gap: 6px;
  pointer-events: auto;
}

.comment-header {
  margin-bottom: 4px;
}

.comment-username {
  display: block;
}

.comment-content {
  flex: 1;
}

.comment-text {
  text-align: left;
}

/* Audio Player Styles */
.audio-wrapper {
  width: 100%;
}

.custom-audio-player {
  width: 100%;
  padding: 10px 0;
}

.audio-controls {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.play-pause-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease, background 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  .play-icon {
    color: #ffffff;
    font-size: 14px;
    line-height: 1;
  }
}

.wave-timeline-container {
  flex: 1;
  cursor: pointer;
  padding: 8px 0;
}

.wave-timeline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  gap: 2px;
}

.wave-bar {
  flex-shrink: 0;
}

.speed-btn {
  width: 50px;
  height: 26px;
  border: none;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease, background 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  .speed-text {
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    font-family: "Gobold Regular", sans-serif;
  }
}
</style>
